import { queryKeys } from "@listto/shared";

/**
 * P23/CC-60 · P25 — the three session revocation callbacks, OUTSIDE the
 * provider to be able to test them deterministically.
 *
 * They lived inline in `providers/boot-manager.tsx` and hid two of the three
 * links of the defect "revocation lies":
 *
 *  1. `onRevokeSession` called `api.POST(...)` **without checking `.error`**.
 *     `openapi-fetch` DOES NOT throw: a 500 arrives as `{ error }` and the promise
 *     resolved, so the XState actor exited via `onDone` —the SUCCESS
 *     branch— and the UI considered the device evicted. Measured with a forced 500:
 *     dialog closed, zero warnings, row intact and device INSIDE.
 *  3. `onRevokeSuccess` **was not passed**, so not even a real revocation
 *     refreshed the list.
 *
 * Why a separate module and not just the e2e: refresh after a success is also
 * produced, **sometimes**, by `refetchOnWindowFocus` of the list when
 * the dialog closes. Measured here: with link 3 reverted the e2e turns red
 * 2 out of 3 times. A guard that fails 1 out of 3 is not a guard — this test is
 * deterministic.
 */

/** Minimum surface needed by the callbacks (injectable in tests). */
export interface RevokeSessionDeps {
  /** POST to `/api/auth/revoke-session`. Returns openapi-fetch shape. */
  revoke: (sessionId: string) => Promise<{ error?: { message?: string } | undefined }>;
  invalidate: (queryKey: readonly unknown[]) => void;
  notifyError: () => void;
}

export interface RevokeSessionCallbacks {
  onRevokeSession: (sessionId: string) => Promise<void>;
  onRevokeSuccess: () => void;
  onRevokeError: () => void;
}

export function createRevokeSessionCallbacks(deps: RevokeSessionDeps): RevokeSessionCallbacks {
  const refreshList = () => {
    deps.invalidate(queryKeys.auth.activeSessions());
  };

  return {
    onRevokeSession: async (sessionId: string) => {
      const { error } = await deps.revoke(sessionId);
      if (error) {
        // Converting openapi-fetch's `{error}` into a REAL exception is
        // the only thing that makes the actor take the `onError` branch.
        throw new Error(error.message ?? "revoke-session failed");
      }
    },
    onRevokeSuccess: refreshList,
    onRevokeError: () => {
      deps.notifyError();
      // The list might have become out of sync: request it again so that
      // it reflects the truth of the server, not what we thought we did.
      refreshList();
    },
  };
}
